/**
 * Validation util
 */
define(function(require, exports, module) {
    'use strict';

    const $ = require('j');
    const user = require('user');
    const i18n = require('i18n');
    const util = require('utils');
    const _ = require('underscore');
    const config = require('config');
    const dom = require('util/dom.util');
    const dateUtil = require('util/date.util');

    /**
     * @typedef {Object} Deferred
     */

    module.exports = {
        _cachedDefaultRules: null,
        _cachedDefaultRulesLang: null,
        /**
         * Get default validation rules for inputs
         * @return {Object}
         */
        get defaultRules() {
            const lang = user.get('lang');
            const regulation = config.get('regulation');

            if (!this._cachedDefaultRules || this._cachedDefaultRulesLang !== lang + regulation) {
                // noinspection RegExpRedundantEscape
                var result = {
                    required: {
                        priority: 0,
                        message: i18n.t('playtech.mobile.system.tooltip.required')
                    },
                    email: {
                        priority: 100,
                        regexp: /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i,
                        message: i18n.t('playtech.mobile.system.tooltip.email')
                    },
                    number: {
                        priority: 100,
                        regexp: /^$|^\-?((\d+\.?$)|(\.\d+$)|(\d+\.\d+$))/,
                        message: i18n.t('playtech.mobile.system.tooltip.number')
                    },
                    amount: {
                        priority: 100,
                        // any digit except 0 + 0-many digits OR
                        // any digit except 0 + 0-many digits + (dot AND 1 or 2 digits) OR
                        // zero AND dot AND 1 or 2 digits
                        regexp: /^[1-9]\d*$|^[1-9]\d*(?:\.\d{1,2})?$|^0(?:\.\d{1,2}){1}$/,
                        message: i18n.t('playtech.mobile.system.tooltip.amount')
                    },
                    length: {
                        priority: 200,
                        min: 1,
                        max: 10
                    },
                    range: {
                        priority: 300,
                        min: 0,
                        max: 0
                    },
                    exclusion: {
                        priority: 300,
                    },
                    dateRange: {
                        priority: 300,
                        min: 0,
                        max: 0
                    },
                    regexp: {
                        priority: 500,
                        regexp: /^\w+&/,
                        message: i18n.t('playtech.mobile.system.tooltip.regexp')
                    },
                    fn: {
                        priority: 1000,
                        /**
                         * @abstract
                         */
                        fn: function($inputs, cfg, form, dfd) { // eslint-disable-line
                            return true;
                        }
                    },
                    ajax: {
                        priority: 2000,
                        fn: function($inputs, cfg, form, dfd) {
                            dfd.resolve();
                            return dfd;
                        }
                    }
                };

                // Dynamic translation
                Object.defineProperties(result.length, {
                    'defaultMessage': {
                        configurable: false,
                        enumerable: false,
                        writable: true,
                        value: i18n.t('playtech.mobile.system.tooltip.length')
                    },
                    'simpleMessage': {
                        configurable: false,
                        enumerable: false,
                        writable: true,
                        value: i18n.t('playtech.mobile.system.tooltip.lengthSimple')
                    },
                    'message': {
                        configurable: false,
                        enumerable: true,
                        get: function() {
                            var message = this.min === this.max ? this.simpleMessage : this.defaultMessage;
                            return util.string.format(message, this.min, this.max);
                        },
                        set: function(value) {
                            this.simpleMessage = value;
                            this.defaultMessage = value;
                        }
                    }
                });
                Object.defineProperties(result.range, {
                    'defaultMessage': {
                        configurable: false,
                        enumerable: false,
                        writable: true,
                        value: i18n.t('playtech.mobile.system.tooltip.range')
                    },
                    'message': {
                        configurable: false,
                        enumerable: true,
                        get: function() {
                            return util.string.format(this.defaultMessage, this.min, this.max);
                        },
                        set: function(value) {
                            this.defaultMessage = value;
                        }
                    }
                });

                this._cachedDefaultRules = result;
                this._cachedDefaultRulesLang = lang + regulation;
            }

            return this._cachedDefaultRules;
        },

        /**
         * Validation methods for validation validationRules
         * @type {Object}
         */
        methods: {
            required: function(options) {
                var iterator = options.$inputs && options.$inputs[0] && options.$inputs[0].type === 'radio'
                    ? 'some'
                    : 'every';
                return options.values[iterator](function(value) {
                    return _.isBoolean(value)
                        ? value
                        : value.toString().trim() !== '' && value !== null;
                });
            },
            regexp: function(options) {
                return options.values.every(function(value) {
                    return !value && _.isString(value)
                        ? true
                        : options.config.regexp.test(value);
                });
            },
            length: function(options) {
                return options.values.every(function(value) {
                    if (!value && _.isString(value)) {
                        return true;
                    }
                    var strLength = value.length;
                    return strLength >= options.config.min && strLength <= options.config.max;
                });
            },
            fn: function(options) {
                var keys = [];

                if ('$inputs' in options) {
                    keys = ['$inputs', 'config', 'form', 'dfd', 'event', 'data'];
                } else {
                    keys = ['values', 'config', 'data'];

                    if ('customData' in options) {
                        keys.push('customData');
                    }
                }

                var args = keys.map(function(key) {
                    return options[key];
                });

                return options.config.fn.apply(options.config, args);
            },
            email: function(options) {
                return this.regexp(options);
            },
            ajax: function(options) {
                let keys = [];
                const { form, $inputs = [] } = options || {};
                const fieldName = $inputs[0].name;

                if ('$inputs' in options) {
                    keys = ['$inputs', 'config', 'form', 'dfd', 'event', 'data'];
                } else {
                    keys = ['values', 'config', 'data'];

                    if ('customData' in options) {
                        keys.push('customData');
                    }
                }

                if (form && fieldName) {
                    form.addValidationInProgressFields([fieldName]);
                }

                const args = keys.map(function(key) {
                    return options[key];
                });

                return options.config.fn.apply(options.config, args)
                    .always(() => {
                        if (form && fieldName) {
                            form.removeValidationInProgressFields([fieldName]);
                        }
                    });
            },
            number: function(options) {
                return this.regexp(options);
            },
            amount: function(options) {
                return this.regexp(options);
            },
            range: function(options) {
                return options.values.every(function(value) {
                    var val = window.Playtech.API.formatter.delocalizeNumber(value);
                    if (val === '' || isNaN(val)) {
                        return true;
                    }
                    val = Number(val);
                    return options.config.min <= val && val <= options.config.max;
                });
            },
            dateRange: function(options) {
                const dateStr = new Date(options.values.reverse().join('-'));
                const minDateToUnix = dateUtil.parseStringDateToUnixFormat(options.config.min);
                const maxDateToUnix = dateUtil.parseStringDateToUnixFormat(options.config.max);
                const minFullDate = dateUtil.getStartOfTheDay(new Date(minDateToUnix));
                const maxFullDate = dateUtil.getEndOfTheDay(new Date(maxDateToUnix));

                if (options.values.includes('')) {
                    const fieldType = options.config.fieldType === 'dateOfBirthInner' ? 'dateOfBirth' : options.config.fieldType;

                    options.config.message = `playtech.mobile.system.user.tooltip.${fieldType}.required`;
                    return false;
                }

                if (dateStr < minFullDate) {
                    options.config.message = options.config?.dateRangeMinMessage || 'playtech.mobile.system.user.tooltip.datePicker.range';
                    return false;
                }

                if (dateStr > maxFullDate) {
                    options.config.message = options.config?.dateRangeMaxMessage || 'playtech.mobile.system.user.tooltip.datePicker.range';
                    return false;
                }

                return true;
            },
            exclusion: function(options) {
                const { values = [], config: { exclusionFields = [], dateFields = [] } = {}, data = {} } = options || {};
                const getValue = (val) => {
                    if (typeof val === 'string') {
                        return val.toLowerCase();
                    }

                    if (typeof val === 'number') {
                        return String(val);
                    }

                    return '';
                };

                const fieldValue = getValue(values[0]);

                if (!fieldValue) {
                    return true;
                }

                const isIncludeRegularValue = exclusionFields
                    .filter(name => !dateFields.includes(name))
                    .some((name) => {
                        const formValue = getValue(data[name]);

                        return formValue && fieldValue.includes(formValue);
                    });

                const isIncludeDateValue = exclusionFields
                    .filter(name => dateFields.includes(name))
                    .some((name) => {
                        const formValue = getValue(data[name]);

                        if (!formValue) {
                            return false;
                        }

                        const [year, month, day] = formValue.split('-');

                        if (![year, month, day].every(Boolean)) {
                            return false;
                        }

                        return fieldValue.includes(`${day}${month}${year}`) || fieldValue.includes(`${year}${month}${day}`);
                    });

                return !isIncludeRegularValue && !isIncludeDateValue;
            }
        },

        /**
         * Get validation rules for selected field
         * @param  {string} validationType
         * @param {Object} fieldConfig
         * @return {Object}
         */
        getValidationConfig: function(validationType, fieldConfig) {
            var cfg = this.defaultRules;
            var mergedConfig = {};

            fieldConfig = _.isFunction(fieldConfig) ? fieldConfig() : fieldConfig;
            fieldConfig = fieldConfig[validationType];

            _.keys(fieldConfig).forEach(function(rule) {
                if (fieldConfig[rule] === true) {
                    mergedConfig[rule] = util.cloneObject(cfg[rule]);
                } else if (fieldConfig[rule] !== false) {
                    mergedConfig[rule] = _.extend(util.cloneObject(cfg[rule]), fieldConfig[rule]);
                }
            });

            return mergedConfig;
        },

        /**
         * Validate separate DOM field
         * @param  {$} $field
         * @param  {Object} options
         * @return {Deferred}
         */
        validateDomField: function($field, options) {
            if (!options || !options.validationConfig) {
                return $.Deferred().reject();
            }

            var inputs = ['input', 'select', 'textarea'];
            var selector = inputs.reduce(function(memo, input) {
                return memo.concat(
                    input + ':not([disabled])',
                    input + '.progress:disabled'
                );
            }, []).join(', ');
            var validationType = $field[0].dataset.validationType || '';
            var $inputs = $($field.find(selector).filter(function(el) {
                return !el.classList.contains('no-validate');
            }));

            if (!$inputs.length) {
                return $.Deferred().resolve();
            }

            $inputs.sort((a, b) => {
                const getOrder = (input) => {
                    switch (input.dataset.datePart) {
                        case 'day': return 1;
                        case 'month': return 2;
                        case 'year': return 3;
                        default: return 0;
                    }
                };

                return getOrder(a) - getOrder(b);
            });

            var values = $inputs.map(dom.getInputValidationValue);

            return this._validateField(_.extend({}, options, {
                values: values,
                $inputs: $inputs,
                validationType: validationType,
                validationConfig: options.validationConfig
            }));
        },

        /**
         * Validate value according to config
         * @param  {any} value
         * @param  {Object} cfg
         *
         * validation.validateField('TEST@TEST.COM, { email: true });
         *
         * @return {Deferred|boolean}
         */
        validateField: function(value, cfg) {
            var result = this.validate({
                data: {
                    fieldName: value
                },
                config: {
                    fieldName: cfg
                }
            });
            if (result.state() !== 'pending') {
                return result.state() === 'resolved';
            }

            return result.promise();
        },

        /**
         * Validate object with data according to config
         * @param  {Object} options -- validate data in format:
         * {
         *     data: {
         *         firstName: 'John',
         *         lastName: 'Doe'
         *     },
         *     config: {
         *         firstName: {
         *             required: true
         *             fn: {
         *                  fn: function() { return true; }
         *             }
         *         },
         *         lastName: {
         *             required: true
         *         }
         *     }
         * }
         * @return {Deferred}
         */
        validate: function(options) {
            if (!options || !options.data || !options.config) {
                throw new Error('No validation config');
            }

            var state = Object.keys(options.data).map(function(validationType) {
                var values = options.data[validationType];
                var validationData;

                values = _.isArray(values) ? values : [values];

                validationData = {
                    values: values,
                    data: options.data,
                    validationType: validationType,
                    validationConfig: options.config
                };

                // additional info for validation
                if ('customData' in options) {
                    validationData.customData = options.customData;
                }

                return this._validateField(validationData);
            }, this);

            return $.when(state);
        },

        /**
         * Main validation logic
         * @private
         * @param  {Object} options
         * @return {Deferred}
         */
        _validateField: function(options) {
            var dfd = $.Deferred();
            var cfg = this.getValidationConfig(options.validationType, options.validationConfig);
            var validationRules = _.keys(cfg);
            var currentConfig = {};

            // Validation rule order
            validationRules.sort(function(a, b) {
                var rule1 = cfg[a];
                var rule2 = cfg[b];

                if (rule1.priority == null) {
                    return 1;
                }

                if (rule2.priority == null) {
                    return -1;
                }

                if (rule1.priority === rule2.priority) {
                    return 0;
                }

                return rule1.priority - rule2.priority;
            });
            var result = validationRules.every(function(type) {
                currentConfig = cfg[type];
                if (type !== 'ajax') {
                    return this.methods[type](
                        _.extend({}, _.omit(options, 'validationConfig'), {
                            config: currentConfig,
                            dfd: dfd
                        })
                    );
                }
                return true;
            }, this);

            if (result) {
                if (validationRules.includes('ajax')) {
                    this.methods.ajax(_.extend({}, _.omit(options, 'validationConfig'), {
                        config: cfg.ajax,
                        dfd: dfd
                    }));
                } else {
                    dfd.resolve();
                }
            } else {
                dfd.reject(currentConfig);
            }

            return dfd.promise();
        }

    };
});
